import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { View, Pressable, useWindowDimensions, Text } from 'react-native';
import { createPortal } from 'react-dom';
import { t } from 'i18next';
import { useColorScheme } from 'nativewind';
import RenderHtml from 'react-native-render-html';
import { useNavigation, useLinkTo } from '@react-navigation/native';
import svgIcons from '../../assets';
import { HelpdeskIssueDetailModal, Icon, ModalInfo, Tooltip, WebViewModal } from '../index';
import { HelpdeskIssueSearchItem } from '../../client/interfaces';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { cln } from '../../utils/classnames';
import { isMobile, isNative } from '../../utils/responsive';
import { handleHTMLLinkPress, handleLinkPress } from '../../utils';
import { highlightHtml } from '../../utils/highlightHtml';
import analytics from '../../utils/analytics';

const MemoizedRenderHtml = React.memo(RenderHtml);

/**
 * ClickUp matches query params to each field's `display_name` (case-sensitive).
 * Special characters in labels are unreliable, so we send common variants.
 * Prefer renaming form fields to plain "Name" / "Email" in ClickUp.
 * @see https://help.clickup.com/hc/en-us/articles/6310247500055-Hide-Form-questions
 */
const CLICKUP_NAME_FIELD_KEYS = ['Name', 'Név / Name'];
const CLICKUP_EMAIL_FIELD_KEYS = [
  'Email',
  'E-mail / Email',
  'email',
  'E-mail cím / E-mail address',
];
const CLICKUP_NEPTUN_CODE_FIELD_KEYS = [
  'Neptun-kód / Neptun code',
  'Neptun Code',
  'Neptun kód / Neptun Code',
  'Neptun kód / Neptun ID',
];

const buildPrefilledCtaUrl = (
  baseUrl: string,
  name?: string | null,
  email?: string | null,
  neptunCode?: string | null,
): string => {
  if (!baseUrl) return baseUrl;

  const params: string[] = [];
  if (name) {
    CLICKUP_NAME_FIELD_KEYS.forEach((key) => {
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(name)}`);
    });
  }
  if (email) {
    CLICKUP_EMAIL_FIELD_KEYS.forEach((key) => {
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(email)}`);
    });
  }
  if (neptunCode) {
    CLICKUP_NEPTUN_CODE_FIELD_KEYS.forEach((key) => {
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(neptunCode)}`);
    });
  }
  if (params.length === 0) return baseUrl;

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${params.join('&')}`;
};

interface Props {
  item: HelpdeskIssueSearchItem;
  onDetailsPress?: () => void;
  cardWidth?: string;
  searchText?: string;
}

const HelpdeskIssueCard: React.FC<Props> = ({ item, onDetailsPress, cardWidth, searchText }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [modalInfoVisible, setModalInfoVisible] = useState(false);
  const tooltipHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTooltip = () => {
    if (tooltipHideTimer.current) clearTimeout(tooltipHideTimer.current);
    setTooltipVisible(true);
  };
  const hideTooltip = () => {
    tooltipHideTimer.current = setTimeout(() => setTooltipVisible(false), 100);
  };
  // Portal escape: the tooltip is rendered into <body> so it isn't clipped by the
  // section's collapse box (overflow: hidden) or covered by sibling cards/sections.
  // We track the info icon's on-screen rect and place a fixed "ghost" at it so the
  // unchanged Tooltip still anchors beside the icon.
  const iconRef = useRef<View>(null);
  const [tooltipRect, setTooltipRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  useLayoutEffect(() => {
    if (isNative || typeof document === 'undefined' || !tooltipVisible) return;
    // Measure the icon once when the tooltip opens; the fixed ghost stays put after that.
    const el = iconRef.current as unknown as HTMLElement | null;
    if (el) {
      const r = el.getBoundingClientRect();
      setTooltipRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }
    // Dismiss on scroll/resize instead of re-tracking — cheaper and avoids drift.
    const dismiss = () => setTooltipVisible(false);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [tooltipVisible]);
  const { colorScheme } = useColorScheme();
  const { width } = useWindowDimensions();
  const navigation = useNavigation();
  const linkTo = useLinkTo();
  const { auth, me } = useAuth();

  const prefilledCtaLink = useMemo(() => {
    if (!item.ctaLink) return undefined;
    const user = auth?.user;
    const name =
      user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined;
    const email = me?.email || user?.email;
    const neptunCode = user?.neptunCode;
    return buildPrefilledCtaUrl(item.ctaLink, name, email, neptunCode);
  }, [item.ctaLink, auth?.user, me?.email]);

  const tooltipBaseStyle = useMemo(
    () => ({
      color: colorScheme === 'dark' ? '#101010' : '#f6f6f6',
      fontSize: 12,
      fontFamily: 'sans-400',
      lineHeight: 18,
    }),
    [colorScheme],
  );
  const tooltipRenderersProps = useMemo(
    () => ({
      a: {
        onPress: (_event: unknown, href: string) => handleHTMLLinkPress(href, navigation, linkTo),
      },
    }),
    [navigation, linkTo],
  );
  const tooltipTagsStyles = useMemo(
    () => ({
      a: {
        color: colorScheme === 'dark' ? '#623BFF' : '#EDF115',
        textDecorationLine: 'none' as const,
      },
      mark: {
        backgroundColor: colorScheme === 'dark' ? '#EDF115' : '#8509ff',
        color: colorScheme === 'dark' ? '#101010' : '#FAFAFA',
      },
    }),
    [colorScheme],
  );
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [webViewVisible, setWebViewVisible] = useState(false);
  const [actionHovered, setActionHovered] = useState(false);

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <View
      className="bg-neutral-100 dark:bg-neutral-930 z-0 relative"
      style={{ width: cardWidth, zIndex: tooltipVisible ? 10 : 1 } as object}
    >
      <View className={cln(isMobile ? 'pr-3 pl-3 pb-2 pt-3' : 'p-3')}>
        {/* Organization tag */}
        <View className="self-start bg-neutral-200 dark:bg-neutral-910 px-2 py-1 mb-3">
          <Text className="text-neutral-930 dark:text-neutral-100 text-platformSubtitleMobile font-[sans-400]">
            {item.organizationName}
          </Text>
        </View>

        {/* Title row */}
        <View className="flex-row items-start justify-between h-[72px]">
          <Text
            className="text-body sm:text-lead text-neutral-950 dark:text-neutral-50 font-[sans-400] flex-1"
            numberOfLines={3}
          >
            {item.title}
          </Text>
          {item.description && item.ctaLink ? (
            <Pressable
              ref={iconRef}
              testID="helpdesk-issue-card-info"
              className="ml-1"
              style={{ position: 'relative' }}
              onHoverIn={() => !isMobile && showTooltip()}
              onHoverOut={() => !isMobile && hideTooltip()}
              onPress={() => isMobile && setModalInfoVisible(true)}
            >
              <Icon
                icon={
                  searchText && item.description.toLowerCase().includes(searchText.toLowerCase())
                    ? svgIcons.infoIconPrimary
                    : svgIcons.infoIcon
                }
                mobileSize={{ width: 24, height: 24 }}
                webSize={{ width: 24, height: 24 }}
              />
              {tooltipVisible &&
                !isNative &&
                typeof document !== 'undefined' &&
                tooltipRect &&
                createPortal(
                  <Pressable
                    onHoverIn={showTooltip}
                    onHoverOut={hideTooltip}
                    style={
                      {
                        position: 'fixed',
                        top: tooltipRect.top,
                        left: tooltipRect.left,
                        width: tooltipRect.width,
                        height: tooltipRect.height,
                        zIndex: 9999,
                      } as object
                    }
                  >
                    <Tooltip testID="helpdesk-issue-card-tooltip">
                      <MemoizedRenderHtml
                        contentWidth={width}
                        source={{ html: highlightHtml(item.description, searchText) }}
                        baseStyle={tooltipBaseStyle}
                        renderersProps={tooltipRenderersProps}
                        tagsStyles={tooltipTagsStyles}
                      />
                    </Tooltip>
                  </Pressable>,
                  document.body,
                )}
              <ModalInfo
                testID="helpdesk-issue-card-modal"
                showModal={modalInfoVisible}
                closeModal={() => setModalInfoVisible(false)}
                text={item.description}
                search={searchText}
              />
            </Pressable>
          ) : null}
        </View>
      </View>
      <Pressable
        testID="helpdesk-issue-card-pressable"
        onPress={(event) => {
          analytics()
            .logEvent('subtopic_click', {
              subtopic_id: item.helpdeskIssueId,
              title: item.title,
              action: item.ctaLink ? 'open_link' : 'open_details',
              ...(item.ctaLink ? { link: item.ctaLink } : {}),
            })
            .catch((e) => console.error(e));

          if (prefilledCtaLink) {
            if (isNative) {
              setWebViewVisible(true);
            } else {
              // href exposes the prefilled URL (status bar / copy link); open via project helper
              event.preventDefault?.();
              handleLinkPress(prefilledCtaLink);
            }
          } else {
            setDetailsModalVisible(true);
          }
        }}
        onHoverIn={() => setActionHovered(true)}
        onHoverOut={() => setActionHovered(false)}
        className={cln(
          'flex-row w-full p-3 transition-all duration-200 justify-end items-center border-t-[1px] border-neutral-200 dark:border-neutral-910',
          actionHovered && 'bg-neutral-200 dark:bg-neutral-910',
        )}
        href={!isNative && prefilledCtaLink ? prefilledCtaLink : undefined}
        hrefAttrs={
          !isNative && prefilledCtaLink
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : undefined
        }
        // @ts-expect-error web only
        style={{ transition: 'all 200ms ease-in-out', marginTop: 'auto' }}
      >
        <Text className="text-neutral-950 dark:text-neutral-50 text-calendarCardDetails font-[sans-400] mr-2">
          {item.ctaLink ? t('helpdesk:open') : t('helpdesk:details')}
        </Text>
        <Icon
          icon={
            item.ctaLink
              ? svgIcons.externalLink
              : searchText && item.description?.toLowerCase().includes(searchText.toLowerCase())
                ? svgIcons.arrowRightIconPrimary
                : svgIcons.arrowRightIcon
          }
          mobileSize={{ width: 20, height: 20 }}
          webSize={{ width: 20, height: 20 }}
        />
      </Pressable>
      {!item.ctaLink && (
        <HelpdeskIssueDetailModal
          showModal={detailsModalVisible}
          closeModal={() => setDetailsModalVisible(false)}
          title={item.title}
          description={item.description}
          search={searchText}
        />
      )}
      {prefilledCtaLink && isNative && (
        <WebViewModal
          showModal={webViewVisible}
          closeModal={() => setWebViewVisible(false)}
          uri={prefilledCtaLink}
        />
      )}
    </View>
  );
};

export default HelpdeskIssueCard;
